export type RealtimeDiagnosticValue =
  boolean | number | string | null | readonly string[];

export type RealtimeDiagnosticEntry = {
  at: string;
  event: string;
  details: Record<string, RealtimeDiagnosticValue>;
};

const MAX_ENTRIES = 250;
const entries: RealtimeDiagnosticEntry[] = [];
let installed = false;

function shorten(value: string): string {
  return value.length <= 8 ? value : `…${value.slice(-6)}`;
}

function sanitize(
  key: string,
  value: RealtimeDiagnosticValue,
): RealtimeDiagnosticValue {
  const sensitiveIdentifier =
    key.toLowerCase().includes("id") ||
    key.toLowerCase().includes("channel") ||
    key.toLowerCase().includes("session");
  if (!sensitiveIdentifier) return value;
  if (typeof value === "string") return shorten(value);
  if (Array.isArray(value)) return value.map(shorten);
  return value;
}

export function recordRealtimeDiagnostic(
  event: string,
  details: Record<string, RealtimeDiagnosticValue> = {},
): void {
  entries.push({
    at: new Date().toISOString(),
    event,
    details: Object.fromEntries(
      Object.entries(details).map(([key, value]) => [
        key,
        sanitize(key, value),
      ]),
    ),
  });
  if (entries.length > MAX_ENTRIES) {
    entries.splice(0, entries.length - MAX_ENTRIES);
  }
}

export function getRealtimeDiagnostics(): RealtimeDiagnosticEntry[] {
  return entries.map((entry) => ({
    ...entry,
    details: { ...entry.details },
  }));
}

export function clearRealtimeDiagnostics(): void {
  entries.length = 0;
}

export function installRealtimeDiagnostics(): void {
  if (installed || typeof window === "undefined") return;
  const enabled =
    import.meta.env.DEV ||
    new URLSearchParams(window.location.search).get("debug") === "realtime";
  if (!enabled) return;
  installed = true;
  const diagnosticWindow = window as Window & {
    __TRAFFIC_REALTIME__?: {
      snapshot: () => RealtimeDiagnosticEntry[];
      clear: () => void;
    };
  };
  Object.defineProperty(diagnosticWindow, "__TRAFFIC_REALTIME__", {
    configurable: true,
    value: Object.freeze({
      snapshot: getRealtimeDiagnostics,
      clear: clearRealtimeDiagnostics,
    }),
  });
}
